import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import { ArrowLeft, Database, Save } from "lucide-react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "../styles/rich-content.css";
import { cn } from "@/lib/utils";
import { validateImageFile } from "@/services/storage";
import { CmsCollectionService, CmsItemService, type CmsCollection, type CmsItemStatus } from "@/services/cms";

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function parsePath(location: string): { collectionKey: string | null; id: string | null } {
  const newMatch = location.match(/^\/cms\/([^/]+)\/items\/new$/);
  if (newMatch) return { collectionKey: newMatch[1], id: null };
  const editMatch = location.match(/^\/cms\/([^/]+)\/items\/([^/]+)\/edit$/);
  if (editMatch) return { collectionKey: editMatch[1], id: editMatch[2] };
  return { collectionKey: null, id: null };
}

const CmsItemEdit: React.FC = () => {
  const [location, setLocation] = useLocation();
  const { collectionKey, id } = parsePath(location);
  const isEdit = !!id;

  const [collection, setCollection] = useState<CmsCollection | null>(null);
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [slug, setSlug] = useState("");
  const [slugEditedManually, setSlugEditedManually] = useState(false);
  const [status, setStatus] = useState<CmsItemStatus>("draft");
  const [isFeatured, setIsFeatured] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const quillRef = useRef<any>(null);

  useEffect(() => {
    if (collectionKey) load();
  }, [collectionKey, id]);

  useEffect(() => {
    if (successMessage || error) {
      const timer = setTimeout(() => {
        setSuccessMessage("");
        setError("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, error]);

  const load = async () => {
    if (!collectionKey) return;
    setIsLoading(true);
    setError("");
    try {
      const col = await CmsCollectionService.getByKey(collectionKey);
      setCollection(col);

      if (id) {
        const item = await CmsItemService.get(id);
        setValues(item.fieldValues || {});
        setSlug(item.slug || "");
        setSlugEditedManually(true);
        setStatus(item.status);
        setIsFeatured(item.isFeatured);
      }
    } catch (err: any) {
      console.error("Failed to load:", err);
      setError("Failed to load. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const primaryField = useMemo(() => {
    if (!collection) return null;
    return collection.fields.find((f) => f.type === "text") || collection.fields[0] || null;
  }, [collection]);

  const handleFieldChange = (key: string, value: unknown) => {
    setValues((prev) => {
      const next = { ...prev, [key]: value };
      if (!slugEditedManually && collection?.hasSlug && primaryField && key === primaryField.key) {
        setSlug(slugify(String(value || "")));
      }
      return next;
    });
  };

  const handleImageUpload = (key: string) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      const validation = validateImageFile(file, 10);
      if (!validation.isValid) {
        setError(validation.errors.join(", "));
        return;
      }
      const reader = new FileReader();
      reader.onload = () => handleFieldChange(key, String(reader.result || ""));
      reader.readAsDataURL(file);
    };
    input.click();
  };

  const quillImageHandler = () => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const validation = validateImageFile(file, 10);
      if (!validation.isValid) {
        setError(validation.errors.join(", "));
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = String(reader.result || "");
        const editor = quillRef.current?.getEditor?.();
        if (!editor || !dataUrl) return;
        const range = editor.getSelection(true);
        const insertIndex = range ? range.index : editor.getLength();
        editor.insertEmbed(insertIndex, "image", dataUrl, "user");
        editor.setSelection(insertIndex + 1);
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };

  const quillModules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          ["bold", "italic", "underline", "strike"],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ indent: "-1" }, { indent: "+1" }],
          [{ color: [] }, { background: [] }],
          [{ align: [] }],
          ["link", "image"],
          ["blockquote", "code-block"],
          ["clean"],
        ],
        handlers: { image: quillImageHandler },
      },
    }),
    []
  );

  const quillFormats = [
    "header", "bold", "italic", "underline", "strike",
    "list", "bullet", "indent", "color", "background",
    "align", "link", "image", "blockquote", "code-block",
  ];

  const handleSave = async () => {
    if (!collection) return;
    setError("");

    for (const field of collection.fields) {
      const v = values[field.key];
      if (field.required && (v === undefined || v === null || v === "")) {
        setError(`"${field.label}" is required.`);
        return;
      }
    }
    if (collection.hasSlug && !slug.trim()) {
      setError("Slug is required.");
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        slug: collection.hasSlug ? slugify(slug) : undefined,
        fieldValues: values,
        status,
        isFeatured,
      };

      if (isEdit && id) {
        await CmsItemService.update(id, payload);
        setSuccessMessage("Item updated successfully!");
      } else {
        await CmsItemService.create(collection.id, payload);
        setSuccessMessage("Item created successfully!");
        setTimeout(() => setLocation(`/cms/${collection.key}`), 1500);
      }
    } catch (err: any) {
      if (err?.status === 409) {
        setError("An item with that slug already exists. Try a different slug.");
      } else {
        setError(err?.message || "Failed to save item.");
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          <span className="text-gray-600">Loading...</span>
        </div>
      </div>
    );
  }

  if (!collection) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-sm text-red-600">{error || "Collection not found."}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => setLocation(`/cms/${collection.key}`)}
            className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back</span>
          </button>
          <div className="flex items-center gap-3">
            <Database className="h-8 w-8 text-primary shrink-0" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{isEdit ? "Edit Item" : "New Item"}</h1>
              <p className="text-gray-600">{collection.name}</p>
            </div>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className={cn(
            "flex items-center justify-center space-x-2 px-6 py-2 text-sm font-medium rounded-md transition-colors w-full sm:w-auto",
            isSaving ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-primary text-white hover:bg-primary/90"
          )}
        >
          <Save className="h-4 w-4" />
          <span>{isSaving ? "Saving..." : isEdit ? "Update Item" : "Create Item"}</span>
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <p className="text-sm text-green-600">{successMessage}</p>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-4">
            {collection.fields
              .filter((f) => f.type !== "richtext")
              .map((field) => (
                <div key={field.key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {field.label}
                    {field.required ? " *" : ""}
                  </label>

                  {field.type === "text" && (
                    <input
                      type="text"
                      value={String(values[field.key] ?? "")}
                      onChange={(e) => handleFieldChange(field.key, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    />
                  )}

                  {field.type === "textarea" && (
                    <textarea
                      value={String(values[field.key] ?? "")}
                      onChange={(e) => handleFieldChange(field.key, e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    />
                  )}

                  {field.type === "number" && (
                    <input
                      type="number"
                      value={values[field.key] === undefined ? "" : Number(values[field.key])}
                      onChange={(e) => handleFieldChange(field.key, e.target.value === "" ? undefined : Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    />
                  )}

                  {field.type === "date" && (
                    <input
                      type="date"
                      value={values[field.key] ? String(values[field.key]).slice(0, 10) : ""}
                      onChange={(e) => handleFieldChange(field.key, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    />
                  )}

                  {field.type === "boolean" && (
                    <input
                      type="checkbox"
                      checked={!!values[field.key]}
                      onChange={(e) => handleFieldChange(field.key, e.target.checked)}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                  )}

                  {field.type === "link" && (
                    <input
                      type="text"
                      value={String(values[field.key] ?? "")}
                      onChange={(e) => handleFieldChange(field.key, e.target.value)}
                      placeholder="https://..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    />
                  )}

                  {field.type === "image" && (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={String(values[field.key] ?? "")}
                          onChange={(e) => handleFieldChange(field.key, e.target.value)}
                          placeholder="https://... or upload"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                        />
                        <button
                          type="button"
                          onClick={() => handleImageUpload(field.key)}
                          className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 whitespace-nowrap"
                        >
                          Upload
                        </button>
                      </div>
                      {!!values[field.key] && (
                        <img
                          src={String(values[field.key])}
                          alt=""
                          className="h-28 w-44 object-cover rounded-md border border-gray-200"
                          onError={(e) => ((e.target as HTMLImageElement).style.display = "none")}
                        />
                      )}
                    </div>
                  )}

                  {field.type === "select" && (
                    <select
                      value={String(values[field.key] ?? "")}
                      onChange={(e) => handleFieldChange(field.key, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    >
                      <option value="" disabled>
                        Select {field.label}
                      </option>
                      {(field.options || []).map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  )}

                  {field.helpText && <p className="text-xs text-gray-500 mt-1">{field.helpText}</p>}
                </div>
              ))}
          </div>

          <div className="space-y-4">
            {collection.hasSlug && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Slug *</label>
                <input
                  type="text"
                  value={slug}
                  onChange={(e) => {
                    setSlugEditedManually(true);
                    setSlug(e.target.value);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                />
                <p className="text-xs text-gray-500 mt-1">Auto-filled from the first field — edit for a custom URL.</p>
              </div>
            )}

            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={isFeatured}
                  onChange={(e) => setIsFeatured(e.target.checked)}
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Featured item</span>
              </label>
              <p className="text-xs text-gray-500 mt-1">Only one item per collection can be featured.</p>
            </div>

            <div className="flex items-center">
              <input
                id="isPublished"
                type="checkbox"
                checked={status === "published"}
                onChange={(e) => setStatus(e.target.checked ? "published" : "draft")}
                className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
              />
              <label htmlFor="isPublished" className="ml-2 block text-sm text-gray-700">
                Publish immediately
              </label>
            </div>
            <p className="text-xs text-gray-500">Published items are visible on the public site.</p>
          </div>
        </div>

        {collection.fields
          .filter((f) => f.type === "richtext")
          .map((field) => (
            <div key={field.key} className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                {field.label}
                {field.required ? " *" : ""}
              </label>
              <div className="bg-white border border-gray-300 rounded-md">
                <ReactQuill
                  theme="snow"
                  ref={quillRef}
                  value={String(values[field.key] ?? "")}
                  onChange={(value) => handleFieldChange(field.key, value)}
                  modules={quillModules}
                  formats={quillFormats}
                  style={{ minHeight: "400px" }}
                />
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default CmsItemEdit;
